import { useEffect, useRef, useState } from 'preact/hooks';
import { request, type Session } from '../api/client';
import { saveDestination } from '../state/destinations';
import type { WireCapabilities } from '../api/wire';
import { DecoderUnavailable, scan, scanAvailability, type Provisioning } from '../state/qr';

/** Point this client at a server and hold its credential.
 *
 *  Mirrors `design/handoff/ui_kits/pwa/FirstRunScreen.jsx`: a mark, the name,
 *  two underlined fields, and the two actions pinned to the bottom of the
 *  screen. The kit widens this screen's gutter to 26px because it is the one
 *  surface with neither a rail nor a tab bar to inset against.
 *
 *  The token is stored on this device only. A non-loopback endpoint must be
 *  HTTPS: the service worker needs a secure context, and a bearer token has no
 *  business crossing plaintext.
 */
export function FirstRun({ onConnected }: { onConnected: (session: Session) => void }) {
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<
    { kind: 'idle' | 'checking' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  // The token starts visible, as the kit shows it. Hiding it by default would
  // conceal exactly the mangling — a capitalised first letter, a truncated
  // paste — that makes a scanned or pasted token fail, and on iOS a masked
  // field invites a password manager to autofill over it.
  const [hidden, setHidden] = useState(false);
  const [scanning, setScanning] = useState(false);

  /** Save where the journal lives, without a credential.
   *
   *  Reaching a usable app means capture works: entries queue locally and
   *  replay once a token exists. Reads cannot work and the app says so rather
   *  than showing an error per screen.
   */
  function continueOffline() {
    const trimmedUrl = baseUrl.trim().replace(/\/+$/, '');
    const verdict = checkOrigin(trimmedUrl);
    if (verdict) {
      setStatus({ kind: 'error', message: verdict });
      return;
    }
    saveDestination({ label: trimmedUrl || 'same-origin', baseUrl: trimmedUrl, token: '' });
    onConnected({ baseUrl: trimmedUrl, token: '' });
  }

  async function connect(event: Event) {
    event.preventDefault();
    const trimmedUrl = baseUrl.trim().replace(/\/+$/, '');
    const trimmedToken = token.trim();
    if (!trimmedToken) {
      setStatus({ kind: 'error', message: 'A token is required.' });
      return;
    }
    const verdict = checkOrigin(trimmedUrl);
    if (verdict) {
      setStatus({ kind: 'error', message: verdict });
      return;
    }
    setStatus({ kind: 'checking' });
    const session: Session = { baseUrl: trimmedUrl, token: trimmedToken };
    try {
      const capabilities = (await request<WireCapabilities>(session, '/v1/capabilities')).body;
      saveDestination({
        label: trimmedUrl || 'same-origin',
        baseUrl: trimmedUrl,
        token: trimmedToken,
      });
      // Surfaced so a contract mismatch is visible immediately rather than as a
      // confusing failure three screens later.
      console.info(
        `bujo: contract ${capabilities.contract_version}, schema ${capabilities.schema_version}`,
      );
      onConnected(session);
    } catch (error) {
      setStatus({ kind: 'error', message: describe(error) });
      // A 401 means the server was reached and refused the credential, so the
      // network is fine and the string is wrong. Show it, because on a device
      // the cause is almost always an autocapitalised or truncated paste.
      if (error instanceof Error && error.name === 'ApiError') setHidden(false);
    }
  }

  /** A scan fills both fields at once — that is the point of the QR. */
  function applyScan(provisioning: Provisioning) {
    // An empty `url` is the deployed shape: same-origin, no base URL needed.
    // Overwriting a typed address with it would be wrong, so only a real one
    // replaces what is there.
    if (provisioning.url) setBaseUrl(provisioning.url);
    setToken(provisioning.token);
    setStatus({ kind: 'idle' });
    setScanning(false);
  }

  function openScanner() {
    const blocked = scanAvailability();
    if (blocked) {
      setStatus({ kind: 'error', message: blocked.message });
      return;
    }
    setStatus({ kind: 'idle' });
    setScanning(true);
  }

  return (
    <main
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        // The kit's own gutter for this screen; see the note above.
        padding: '0 26px',
        minHeight: '100%',
        overflowY: 'auto',
      }}
    >
      {scanning ? null : (
      <div style={{ paddingTop: 48 }}>
        {/* The app's mark: the open-task signifier, boxed. */}
        <div
          aria-hidden="true"
          style={{
            width: 46,
            height: 46,
            border: '1px solid var(--bujo-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: 'var(--bujo-fw-regular) var(--bujo-fs-display)/1 var(--bujo-font-mono)',
            color: 'var(--bujo-accent)',
          }}
        >
          &middot;
        </div>
        <h1
          style={{
            font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.3 var(--bujo-font-mono)',
            color: 'var(--bujo-text-primary)',
            margin: 'var(--bujo-space-6) 0 0',
          }}
        >
          BuJo
        </h1>
        <p
          style={{
            font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-prose) var(--bujo-font-sans)',
            color: 'var(--bujo-text-dim)',
            margin: 'var(--bujo-space-2) 0 0',
            maxWidth: '30ch',
          }}
        >
          One journal, three clients. Point this one at your server and it picks up the same log.
        </p>
      </div>
      )}

      {scanning ? (
        <Scanner
          onCancel={() => setScanning(false)}
          onRead={applyScan}
          onFail={(message) => {
            setScanning(false);
            setStatus({ kind: 'error', message });
          }}
        />
      ) : (
        <form
          onSubmit={connect}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <Label style={{ padding: '36px 0 10px' }}>SERVER URL</Label>
          <input
            value={baseUrl}
            onInput={(e) => setBaseUrl((e.target as HTMLInputElement).value)}
            placeholder="https://journal.example.com"
            aria-label="Server URL"
            autocomplete="url"
            inputMode="url"
            spellcheck={false}
            autocapitalize="none"
            autocorrect="off"
            enterkeyhint="next"
            style={{ ...fieldInput, borderBottom: '1px solid var(--bujo-accent)' }}
          />

          <Label style={{ padding: '26px 0 10px' }}>API TOKEN</Label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--bujo-space-3)',
              borderBottom: '1px solid var(--bujo-rule-hairline)',
            }}
          >
            <input
              value={token}
              onInput={(e) => setToken((e.target as HTMLInputElement).value)}
              type={hidden ? 'password' : 'text'}
              placeholder="paste or scan"
              aria-label="API token"
              autocomplete="off"
              spellcheck={false}
              autocapitalize="none"
              autocorrect="off"
              enterkeyhint="go"
              style={{ ...fieldInput, flex: 1, minWidth: 0, borderBottom: 0 }}
            />
            {/* A real button. The kit draws this as a bare `<span>` with no
                handler at all — one more entry in the defect table in
                components/README.md, and the worst place for it, since this is
                the only affordance for the flow the hint below recommends. */}
            <button
              type="button"
              onClick={openScanner}
              style={{
                minHeight: 'var(--bujo-row-min)',
                padding: '0 var(--bujo-space-1)',
                background: 'none',
                border: 0,
                font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
                letterSpacing: 'var(--bujo-ls-badge)',
                color: 'var(--bujo-accent)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              SCAN QR
            </button>
          </div>

          {token ? (
            <div
              style={{
                display: 'flex',
                gap: 'var(--bujo-space-3)',
                alignItems: 'baseline',
                marginTop: 'var(--bujo-space-2)',
              }}
            >
              <button
                type="button"
                onClick={() => setHidden(!hidden)}
                style={{
                  minHeight: 'var(--bujo-row-min)',
                  background: 'none',
                  border: 0,
                  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1 var(--bujo-font-mono)',
                  color: 'var(--bujo-accent)',
                  cursor: 'pointer',
                }}
              >
                {hidden ? 'show' : 'hide'}
              </button>
              <span
                style={{
                  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1 var(--bujo-font-mono)',
                  color: 'var(--bujo-text-meta)',
                }}
              >
                {token.length} char{token.length === 1 ? '' : 's'}
                {token !== token.trim() ? ' · has surrounding space' : ''}
                {/[A-Z]/.test(token) ? ' · contains capitals' : ''}
              </span>
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: 'var(--bujo-space-2)',
              alignItems: 'flex-start',
              marginTop: 'var(--bujo-space-5)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.5 var(--bujo-font-mono)',
                color: 'var(--bujo-label)',
              }}
            >
              &middot;
            </span>
            <span
              style={{
                font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.6 var(--bujo-font-sans)',
                color: 'var(--bujo-text-meta)',
              }}
            >
              The token is stored on this device only.{' '}
              <span style={{ fontFamily: 'var(--bujo-font-mono)', color: 'var(--bujo-text-dim)' }}>
                bujo token new --qr
              </span>{' '}
              in the CLI prints a code to scan.
            </span>
          </div>

          {status.kind === 'error' ? (
            <p
              role="alert"
              style={{
                margin: 'var(--bujo-space-4) 0 0',
                font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/var(--bujo-lh-row) var(--bujo-font-mono)',
                color: 'var(--bujo-state-error)',
              }}
            >
              ! {status.message}
            </p>
          ) : null}

          {/* Pushes the two actions to the bottom of the screen, as the kit
              does, so the primary action sits in the thumb arc. */}
          <div style={{ flex: 1, minHeight: 24 }} />

          <button
            type="submit"
            disabled={status.kind === 'checking'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 52,
              border: '1px solid var(--bujo-accent)',
              background: 'var(--bujo-select-ground)',
              font: 'var(--bujo-fw-bold) var(--bujo-fs-micro)/1 var(--bujo-font-mono)',
              letterSpacing: 'var(--bujo-ls-label)',
              color: 'var(--bujo-accent)',
              marginBottom: 'var(--bujo-space-3)',
              cursor: 'pointer',
            }}
          >
            {status.kind === 'checking' ? 'CONNECTING' : 'CONNECT'}
          </button>

          {/* Always available, as the kit has it. Capture works offline and
              syncs when the server is reachable, so a token is not a
              prerequisite for starting to use the journal. */}
          <button
            type="button"
            onClick={continueOffline}
            style={{
              minHeight: 48,
              background: 'none',
              border: 0,
              font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
              color: 'var(--bujo-text-meta)',
              marginBottom: 'calc(var(--bujo-space-5) + env(safe-area-inset-bottom))',
              cursor: 'pointer',
            }}
          >
            Continue offline &middot; sync later
          </button>
        </form>
      )}
    </main>
  );
}

/** The camera viewfinder.
 *
 *  Mounted only while scanning, which is what keeps `jsqr` out of the initial
 *  bundle: the dynamic import lives in `scan`, and nothing calls it until this
 *  component exists.
 */
function Scanner({
  onRead,
  onCancel,
  onFail,
}: {
  onRead: (provisioning: Provisioning) => void;
  onCancel: () => void;
  onFail: (message: string) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = video.current;
    if (!element) return;
    const controller = new AbortController();
    let cancelled = false;
    scan(element, controller.signal)
      .then((provisioning) => {
        // A null result means the scan was aborted, not that it failed.
        if (cancelled || !provisioning) return;
        onRead(provisioning);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        onFail(describeScanFailure(error));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Mount once. Re-running would restart the camera on every parent render.
  }, []);

  return (
    <section
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 36,
        minHeight: 0,
      }}
    >
      <div
        style={{
          font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
          letterSpacing: 'var(--bujo-ls-label)',
          color: 'var(--bujo-label)',
          paddingBottom: 10,
        }}
      >
        SCAN QR
      </div>
      <div
        style={{
          border: '1px solid var(--bujo-accent)',
          background: 'var(--bujo-surface-sheet)',
          // Square where there is room. The cap matters more than the shape:
          // at 320x640 a full square pushed `Cancel` — the only way out of
          // this mode — below the fold. `cover` crops rather than letterboxes.
          aspectRatio: '1 / 1',
          maxHeight: '52vh',
          overflow: 'hidden',
        }}
      >
        <video
          ref={video}
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
      <p
        style={{
          font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.6 var(--bujo-font-sans)',
          color: 'var(--bujo-text-meta)',
          margin: 'var(--bujo-space-4) 0 0',
        }}
      >
        Point the camera at the code printed by{' '}
        <span style={{ fontFamily: 'var(--bujo-font-mono)', color: 'var(--bujo-text-dim)' }}>
          bujo token new --qr
        </span>
        . It fills in both the address and the token.
      </p>
      <div style={{ flex: 1, minHeight: 24 }} />
      <button
        type="button"
        onClick={onCancel}
        style={{
          minHeight: 48,
          background: 'none',
          border: 0,
          font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
          color: 'var(--bujo-text-meta)',
          marginBottom: 'calc(var(--bujo-space-5) + env(safe-area-inset-bottom))',
          cursor: 'pointer',
        }}
      >
        Cancel &middot; type it instead
      </button>
    </section>
  );
}

/** Why the scan stopped, in terms of what the reader can do about it. */
function describeScanFailure(error: unknown): string {
  if (error instanceof DecoderUnavailable) {
    // Distinct from a camera fault: the code reader is fetched on demand, so
    // this is what an offline first scan looks like. Saying "the camera could
    // not be opened" would send someone to check permissions for nothing.
    return 'The code reader could not be downloaded — this first scan needs a connection. Paste the token instead.';
  }
  if (error instanceof Error && error.name === 'NotAllowedError') {
    return 'Camera access was refused. Paste the token instead.';
  }
  if (error instanceof Error && error.name === 'NotFoundError') {
    return 'This device reports no camera. Paste the token instead.';
  }
  return `The camera could not be opened: ${error instanceof Error ? error.message : String(error)}`;
}

/** The kit's section label: small, letter-spaced, teal. */
function Label({ children, style }: { children: string; style?: Record<string, unknown> }) {
  return (
    <div
      style={{
        font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
        letterSpacing: 'var(--bujo-ls-label)',
        color: 'var(--bujo-label)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Underline, not a box. The kit draws fields as a single rule. */
const fieldInput = {
  minHeight: 'var(--bujo-row-min)',
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
  outline: 'none',
  color: 'var(--bujo-text-primary)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-entry)/1 var(--bujo-font-mono)',
  padding: 0,
  borderRadius: 0,
};

/** Why this address is unacceptable, or null if it is fine.
 *
 *  Blank is the normal answer — the app is served by the same host as its API,
 *  so it needs no base URL at all.
 *
 *  A bearer token must not cross plaintext to somewhere else, but two http cases
 *  are not that: loopback, and the origin the page was itself loaded from. The
 *  second matters because the ordinary way to test on a device is
 *  `http://192.168.x.x:5173`, and refusing the address the user is already
 *  browsing is nonsense — the token is going exactly where the app came from.
 */
function checkOrigin(raw: string): string | null {
  if (!raw) return null;

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
    ? raw
    : `${location.protocol}//${raw}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return `That is not a valid address. Try https://journal.example.com, or leave it blank if this page is served by your server.`;
  }

  if (url.protocol === 'https:') return null;
  if (url.protocol !== 'http:') return `${url.protocol} is not supported — use http or https.`;

  const loopback = ['localhost', '127.0.0.1', '[::1]', '::1'].includes(url.hostname);
  if (loopback) return null;
  if (url.origin === location.origin) return null;

  return `${url.origin} is plain http and is not where this page came from, so a token sent there would cross the network in the clear. Use https, or leave this blank to use ${location.origin}.`;
}

function describe(error: unknown): string {
  if (error instanceof Error && error.name === 'ApiError') {
    if (error.message.startsWith('Unauthorized')) {
      return 'The server rejected this token. It was reached, so the address is right — check the token for a capitalised first letter or a truncated paste.';
    }
    return error.message;
  }
  if (error instanceof Error) return `${error.message} — is the server reachable?`;
  return String(error);
}
